import type { DestinationID } from "@pdfking/imposition"
import { Max, Min, ToNumber } from "@pdfking/math"
import type { SinglePagePdf } from "@pdfking/pdfengine"
import type { Media, MediaEntry, PageNumber } from "@pdfking/renderer-api"
import { GroupMatrix } from "@pdfking/renderer-api"
import type { FileName } from "@pdfking/structs"
import type { StringGUID } from "@pdfking/tfunc"
import { Range, RecordCreate, RecordMap } from "@pdfking/tfunc"

import type { FileSchema } from "#public"
import { FileSlotPage } from "#public"

// The pages a `file_range` names, in order: "1-3,5" is 1, 2, 3, 5. A part
// reads from its smallest number to its largest, never below page 1.
const range_pages = (range: string | null | undefined): number[] =>
  `${range || ""}`
    .replace(/[^0-9,-]+/g, "")
    .split(",")
    .map(part => part.split(/[- ]+/g).filter(Boolean).map(ToNumber))
    .filter(numbers => numbers.length)
    .flatMap(numbers => [...Range(Max(Min(...numbers), 1), Max(Max(...numbers), 1))])

/** What fills one slot of a destination: the file and its page, and the
 * file's matrix — the scale the row asks for, about the page's centre. A blank
 * row names no file. The page is what is known of it up front — its boxes and
 * its FileContentGUID — and `get_file` the page itself, asked only by whoever
 * plots or shows it: an upload's split page, or a generated page laid out
 * when it is asked for. */
export type FileSlot = MediaEntry & {
  filename?: FileName
  page_number: PageNumber
  page?: Media & { guid: StringGUID; get_file: () => Promise<SinglePagePdf> }
}

export type SplitFiles = Record<FileName, Record<PageNumber, SinglePagePdf>>

/**
 * The one file binding: each destination's slots, in row order then
 * `file_range` order. Slot `n` of a destination fills the `n`th page the
 * result gives it — and a sheet's media for that destination is numbered the
 * same way — so this is the plotting context's `file_map` as it is, and the
 * pass-through's `FileSlots`.
 */
export const FilesToDestinations = (
  rows: FileSchema[],
  store: SplitFiles
): Record<DestinationID, Record<PageNumber, FileSlot>> => {
  const slots: Record<DestinationID, FileSlot[]> = {}
  for (const row of rows) {
    const filename = (row.file_id || undefined) as FileName | undefined
    const pages = filename ? store[filename] : undefined
    const id = row.file_destination.id as DestinationID
    const matrix = GroupMatrix({ scale: row.file_scale })
    for (const n of range_pages(row.file_range)) {
      const page_number = n as PageNumber
      const page = pages?.[page_number]
      ;(slots[id] ??= []).push({
        filename,
        page_number,
        page: page && FileSlotPage(page),
        matrix,
      })
    }
  }
  return RecordMap(
    list => RecordCreate(list.map((slot, i) => [(i + 1) as PageNumber, slot] as const)),
    slots
  )
}
